import { ErrorBlockVariant } from 'src/app/shared/components/error-block/error-block.component'
import { ErrorContext, PresentedError, toPresentedError } from 'src/utils/presented-error'

/**
 * Error-system showcase, organised by placement tier. Pure `@Input` usage, so no service
 * patches — the arrays only exist to keep the template loops short.
 *
 * Every payload is verbatim from the toast tester (which mirrors arpio-api's
 * `restore_api.py` and `arpio_core/issues` raises) and is run through `toPresentedError`,
 * so the preview and production cannot drift. Adding a matcher to the routing table means
 * adding its payload here.
 */
export interface ErrorShowcaseEntry {
  key: string
  /** What the reviewer is looking at. */
  label: string
  /** Why it renders the way it does. */
  note: string
  /** The payload, so a reviewer can match a block to a backend string. */
  payload: string
  variant: ErrorBlockVariant
  error: PresentedError
  /** The untouched error object, replayed through UiService by the Trigger button. */
  raw: unknown
  /** What the tier's real host would supply. Re-derived on trigger so the block matches. */
  context: ErrorContext
}

export interface ErrorShowcaseTier {
  key: string
  title: string
  /** The rule the tier exists to enforce. */
  rule: string
  entries: ErrorShowcaseEntry[]
}

const api = (responseCode: number, message?: string) => ({
  responseCode,
  ...(message === undefined ? {} : { body: { message } }),
})

/**
 * `context` models what the tier's real host supplies. Codes with no substring matcher only
 * reach `surface`/`object` when a host is mounted to own them, so a dialog or row preview
 * has to pass `anchored` or it previews the toast fallback instead.
 */
const entry = (
  key: string,
  label: string,
  note: string,
  raw: unknown,
  variant: ErrorBlockVariant = 'block',
  context: ErrorContext = {}
): ErrorShowcaseEntry => {
  const asApi = raw as { responseCode?: number; body?: { message?: string } }
  const code = asApi.responseCode
  const message = asApi.body?.message ?? (raw as { detailedMessage?: string }).detailedMessage
  return {
    key,
    label,
    note,
    payload: [code === undefined ? null : String(code), message].filter(Boolean).join(' · ') || '(client-raised)',
    variant,
    error: toPresentedError(raw, context),
    raw,
    context,
  }
}

const ANCHORED: ErrorContext = { anchored: true }

/**
 * The Tier 3 hosts the preview mounts. Entries name one so a trigger is claimed by the panel
 * or the row it belongs to; `anchored` stays for the static build, which has no registry to
 * ask. `announceApiError` recomputes it from the mounted hosts on the way through.
 */
export const SHOWCASE_OBJECT_PANEL = 'showcase-object-panel'
export const SHOWCASE_OBJECT_ROW = 'showcase-object-row'

const ON_PANEL: ErrorContext = { objectId: SHOWCASE_OBJECT_PANEL, anchored: true }
const ON_ROW: ErrorContext = { objectId: SHOWCASE_OBJECT_ROW, anchored: true }

// ── Real payloads ──────────────────────────────────────────────────────────
const MULTI_APP =
  'Application app-prod-web cannot be restored because of the following issues:\n' +
  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n\n' +
  'Application app-prod-api cannot be restored because of the following issues:\n' +
  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.'

const SINGLE_APP =
  'Application app-prod-web cannot be restored because of the following issues:\n' +
  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.'

// Six applications, to prove the row list scrolls rather than the text clamping.
const SIX_APPS = ['web', 'api', 'worker', 'cache', 'search', 'reports']
  .map(
    (name) =>
      `Application app-prod-${name} cannot be restored because of the following issues:\n` +
      '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.'
  )
  .join('\n\n')

const MULTI_ISSUE_APP =
  'Application app-prod-web cannot be restored because of the following issues:\n' +
  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n' +
  '  Recovery is disabled for this application.\n' +
  '  The selected recovery point is no longer available.'

/** The four severities, side by side. Chip and buttons inherit the block's severity. */
export function buildErrorSeverities(): ErrorShowcaseEntry[] {
  return [
    entry('sev-failure', 'Red — something failed', 'A rejection. Nothing was started.', api(422, SINGLE_APP)),
    entry(
      'sev-gating',
      'Gold — gated or throttled',
      'Nothing is broken; the action is blocked or throttled.',
      api(403, 'You must wait at least 90 seconds since the last email was sent.')
    ),
    entry(
      'sev-info',
      'Blue — informational',
      'An empty state, not an error. Teal is allowed here and only here.',
      api(422, 'No applications to restore.')
    ),
    entry('sev-neutral', 'Gray — system state, no fault', 'Nobody erred.', api(401)),
  ]
}

/** Container chrome only. The anatomy is identical in all four. */
export function buildErrorVariants(): ErrorShowcaseEntry[] {
  return [
    entry('var-block', 'block', 'Default. Dialog bodies and object panels.', api(500)),
    entry(
      'var-row',
      'row',
      'Expanded under the table row that triggered it; the row supplies the boundary.',
      api(422, 'Only completed jobs can be retried. Job status: IN_PROGRESS'),
      'row'
    ),
    entry(
      'var-banner',
      'banner',
      'Page-level gate. Tinted, with a severity left rule.',
      api(422, 'This Arpio account is not entitled to restore applications.'),
      'banner'
    ),
    entry('var-toast', 'toast', 'Shell toast. Elevated, capped at 400px.', api(409), 'toast'),
  ]
}

/**
 * One subsection per tier, covering every row of the routing table. `variant` reflects the
 * chrome the tier's real host will use, so a reviewer sees the shipping treatment.
 */
export function buildErrorTiers(): ErrorShowcaseTier[] {
  return [
    {
      key: 'tier1',
      title: 'Tier 1 — Field',
      rule: 'The input that caused it owns the message. Clears on edit, re-enables submit, and never appears in a toast. These carry no button by design: the fix is one keystroke away.',
      entries: [
        entry(
          't1-role',
          'Duplicate role name',
          'The value is wrong and the fix is immediate.',
          api(422, 'Role name already exists in account.')
        ),
        entry(
          't1-saml-expired',
          'SAML metadata has no unexpired descriptor',
          'Long remedial copy needs room. Names what is still working.',
          api(
            422,
            'The SAML metadata does not include a valid (non-expired) EntityDescriptor with an IDPSSODescriptor.'
          )
        ),
        entry(
          't1-saml-unparseable',
          'SAML metadata will not parse',
          'Same field, different remedy.',
          api(422, 'The SAML metadata document could not be parsed or is not valid.')
        ),
        entry(
          't1-saml-multiple',
          'SAML metadata describes several IDPs',
          'The document is valid but ambiguous, so the remedy is to narrow it.',
          api(
            422,
            'The SAML metadata includes more than one IDPSSODescriptor.  The metadata must contain a single EntityDescriptor that has a single IDPSSODescriptor.'
          )
        ),
        entry(
          't1-saml-binding',
          'SAML metadata has no HTTP-POST binding',
          'A provider-side setting rather than a bad document.',
          api(422, 'The IDPSSODescriptor in the SAML metadata does not define an HTTP-POST binding.')
        ),
      ],
    },
    {
      key: 'tier2',
      title: 'Tier 2 — Dialog',
      rule: 'Anything submitted from a modal renders inside that modal, above its footer, so the button that caused it is still visible. Structured failures render as structure. The footer CTA disables with a reason rather than failing again.',
      entries: [
        entry(
          't2-multi',
          'Multi-application launch failure',
          'One row per application, each with its own reason.',
          api(422, MULTI_APP)
        ),
        entry('t2-six', 'Six applications', 'The list scrolls; the copy above it does not grow.', api(422, SIX_APPS)),
        entry(
          't2-single',
          'Single application, one issue',
          'Indented backend lines become bullets.',
          api(422, SINGLE_APP)
        ),
        entry(
          't2-multi-issue',
          'Single application, three issues',
          'The bullet list grows with the payload.',
          api(422, MULTI_ISSUE_APP)
        ),
        entry(
          't2-invalid',
          'Invalid request',
          'Developer-shaped string demoted to the reference line, never the headline.',
          api(422, 'Application app-prod-web cannot be restored because the request was invalid')
        ),
        entry(
          't2-400-field',
          'Missing required field (400)',
          'Same treatment; the raw string still survives below.',
          api(400, 'Required field "applicationId" is missing from the request body.'),
          'block',
          ANCHORED
        ),
        entry(
          't2-verify',
          'Email verification required',
          'Gating, not failure — gold, and the remedy is a button.',
          api(
            403,
            'You must verify your email address before you can add other users to this account.  To verify your email address, click the link in the email you received when you first signed up to use this service.'
          )
        ),
        entry(
          't2-cooldown',
          'Email cooldown',
          'The countdown belongs on the control it throttles.',
          api(403, 'You must wait at least 90 seconds since the last email was sent.')
        ),
        entry(
          't2-500',
          'Server error with support handoff',
          'No remedy exists, so it is Copy Details plus support.',
          api(500),
          'block',
          ANCHORED
        ),
      ],
    },
    {
      key: 'tier3',
      title: 'Tier 3 — Panel and row',
      rule: 'State conflicts belong on the application, job or resource they are about. The object is on screen and the condition outlives the click, so these persist until state changes and never auto-dismiss.',
      entries: [
        entry(
          't3-disabled',
          'Restore disabled for application',
          'A configuration state, so it stays with the application.',
          api(422, 'Restore is disabled for application app-prod-web'),
          'block',
          ON_PANEL
        ),
        entry(
          't3-job',
          'Retry only completed jobs',
          'A stale view. Refresh is the only honest remedy. No production call site names a row host yet, so this previews the target.',
          api(422, 'Only completed jobs can be retried. Job status: IN_PROGRESS'),
          'row',
          ON_ROW
        ),
        entry(
          't3-409',
          'Conflict with a restore in flight',
          'The conflict this panel can actually receive: the backend names the reason, so Refresh is withheld.',
          api(409, 'The restore request could not be processed because a conflicting restore is already in progress.'),
          'block',
          ON_PANEL
        ),
        entry(
          't3-resume',
          'Resume recovery unavailable',
          'Cause code translated above, kept verbatim below. The remedy is the forward path.',
          api(422, 'Resume recovery is not available for this application. Cause: compatibility'),
          'block',
          ON_PANEL
        ),
        entry(
          't3-lock',
          'Recovery point in use',
          'Contention, not a failure. Gating severity, and the reference keeps the lock message verbatim.',
          api(
            422,
            'Restore request failed because of the following issue: Lock for Recovery Point rp-8f21 is already held. Try again later.'
          ),
          'block',
          ON_PANEL
        ),
      ],
    },
    {
      key: 'tier4',
      title: 'Tier 4 — Page banner',
      rule: 'Account-scope conditions are not caused by a click; they gate everything. They render once at the top of the page and persist, and the relevant controls are already disabled with the same reason.',
      entries: [
        entry(
          't4-entitlement',
          'Account not entitled',
          'The banner explains once; controls do not fail one at a time.',
          api(422, 'This Arpio account is not entitled to restore applications.'),
          'banner'
        ),
        entry('t4-rbac', 'Access denied', 'Red at page level — an action was refused.', api(403), 'banner'),
        entry(
          't4-404',
          'Not found',
          'Gray, because nothing failed: the object simply is not there.',
          api(404),
          'banner'
        ),
        entry(
          't4-empty',
          'No applications to restore',
          'Blue informational. An empty state paired with a forward CTA, not an error.',
          api(422, 'No applications to restore.'),
          'banner'
        ),
      ],
    },
    {
      key: 'tier5',
      title: 'Tier 5 — Global',
      rule: 'Only connectivity, session expiry and async job results legitimately escape the anchor. Everything else must be anchored — a toast means the cause is no longer on screen.',
      entries: [
        entry(
          't5-connectivity',
          'Connection error',
          'Ambient and persistent, self-clearing on reconnect. Ships as an app-wide banner.',
          api(0),
          'banner'
        ),
        entry('t5-401', 'Session expired', 'The one blocking modal, and it is gray: nothing failed.', api(401)),
        entry(
          't5-async',
          'Async recovery failure',
          'Names the object and links to it, because the cause happened minutes ago.',
          {
            message: 'Recovery Failed',
            detailedMessage:
              'The recovery point you selected is no longer available. It may have been deleted or pruned by your retention policy. Refresh and try a different recovery point.',
            sticky: true,
          },
          'toast'
        ),
        entry(
          't5-hubspot',
          'Chat widget unavailable',
          'A quiet neutral chip with an email handoff, not an error block.',
          {
            message: 'Unable to find HubSpot chat widget.',
            detailedMessage: 'Unable to find HubSpot chat widget.',
          },
          'toast'
        ),
        entry('t5-999', 'Unrecognized code', 'The fallback. Sticky, with a support handoff.', api(999), 'toast'),
      ],
    },
  ]
}

/**
 * The bare HTTP code map with no backend body. These are the shapes that reach the user
 * when the API returns a status and nothing else, and they are easy to regress because no
 * substring matcher fires.
 */
export function buildErrorCodeMap(): ErrorShowcaseEntry[] {
  return [0, 400, 401, 403, 404, 409, 422, 500, 999].map((code) =>
    entry(`code-${code}`, `${code}`, 'No response body.', api(code))
  )
}

// ── Tier 1: every validated input in the app, in context ────────────────────

/**
 * One way to put an input into its error state. `backend` fires a real API payload through
 * `UiService`, which is the proof that a field error anchors instead of toasting. `local`
 * writes a value that trips the control's own validator.
 */
export interface FieldInputTrigger {
  key: string
  label: string
  mode: 'backend' | 'local'
  /** backend: the untouched error object. */
  payload?: unknown
  /** local: a value the validator rejects. */
  value?: string
  note: string
}

/** A real input from the app, mirrored with its real validators. */
export interface FieldInputCase {
  key: string
  /** The control, as the user sees it. */
  label: string
  /** Where it appears in production, so a reviewer can go compare. */
  context: string
  controlName: string
  kind: 'text' | 'textarea'
  placeholder: string
  seed: string
  triggers: FieldInputTrigger[]
}

const backend = (key: string, label: string, message: string, note: string): FieldInputTrigger => ({
  key,
  label,
  mode: 'backend',
  payload: api(422, message),
  note,
})

const local = (key: string, label: string, value: string, note: string): FieldInputTrigger => ({
  key,
  label,
  mode: 'local',
  value,
  note,
})

/**
 * One preview: an input, in its error state, with the control that produced it. Cases are
 * flattened case x trigger so every error state is visible at a glance rather than one at a
 * time behind a button — the button re-fires it live through the real pipeline.
 */
export interface FieldInputPreview {
  key: string
  label: string
  context: string
  /** Unique per preview, so each one holds its own error independently. */
  controlName: string
  kind: 'text' | 'textarea'
  placeholder: string
  /** What the control holds here: the rejected value for local, a valid seed for backend. */
  value: string
  mode: 'backend' | 'local'
  payload?: unknown
  triggerLabel: string
  note: string
  /** Validator key, so the component attaches the same rules production uses. */
  rules: string
}

/** Flattens the catalogue so each trigger gets its own input, pre-seeded in that error state. */
export function buildFieldInputPreviews(): FieldInputPreview[] {
  const previews: FieldInputPreview[] = []

  for (const input of buildFieldInputCases()) {
    for (const trigger of input.triggers) {
      previews.push({
        key: `${input.key}--${trigger.key}`,
        label: input.label,
        context: input.context,
        controlName: `${input.controlName}__${trigger.key.replace(/-/g, '_')}`,
        kind: input.kind,
        placeholder: input.placeholder,
        value: trigger.mode === 'local' ? (trigger.value ?? '') : input.seed,
        mode: trigger.mode,
        payload: trigger.payload,
        triggerLabel: trigger.label,
        note: trigger.note,
        rules: input.key,
      })
    }
  }

  return previews
}

export function buildFieldInputCases(): FieldInputCase[] {
  return [
    {
      key: 'role-name',
      label: 'Role name',
      context: 'New role · Edit role',
      controlName: 'roleName',
      kind: 'text',
      placeholder: 'Your role name...',
      seed: 'Recovery Operator',
      triggers: [
        backend(
          'role-name-dupe',
          'Duplicate name (422)',
          'Role name already exists in account.',
          'The case that used to fly to a corner toast while the form stayed on screen.'
        ),
        local('role-name-required', 'Required', '', 'Local validator, already rendered here before this work.'),
      ],
    },
    {
      key: 'external-id',
      label: 'External ID',
      context: 'New role · Edit role',
      controlName: 'externalId',
      kind: 'text',
      placeholder: 'external-role-id',
      seed: 'sso-recovery-operator',
      triggers: [
        local(
          'external-id-pattern',
          'Invalid characters',
          'has spaces & symbols!',
          'Real pattern from FormValidationService.'
        ),
        local('external-id-length', 'Too long', 'x'.repeat(70), 'Real 64-character cap.'),
      ],
    },
    {
      key: 'saml-metadata',
      label: 'Identity provider metadata (XML)',
      context: 'Add / edit identity provider',
      controlName: 'samlMetadata',
      kind: 'textarea',
      placeholder: '<xml> ... </xml>',
      seed: '<EntityDescriptor validUntil="2026-05-01T00:00:00Z" …',
      triggers: [
        backend(
          'saml-expired',
          'No unexpired descriptor (422)',
          'The SAML metadata does not include a valid (non-expired) EntityDescriptor with an IDPSSODescriptor.',
          'Long remedial copy — the reason the overlay style had to go.'
        ),
        backend(
          'saml-unparseable',
          'Will not parse (422)',
          'The SAML metadata document could not be parsed or is not valid.',
          'Same control, a different backend reason.'
        ),
        backend(
          'saml-multiple',
          'Several IDPs (422)',
          'The SAML metadata includes more than one IDPSSODescriptor.  The metadata must contain a single EntityDescriptor that has a single IDPSSODescriptor.',
          'Valid but ambiguous, so the remedy is to narrow the document.'
        ),
        backend(
          'saml-binding',
          'No HTTP-POST binding (422)',
          'The IDPSSODescriptor in the SAML metadata does not define an HTTP-POST binding.',
          'A provider-side setting rather than a bad document.'
        ),
      ],
    },
    {
      key: 'group-name',
      label: 'Group name',
      context: 'New group · Edit group',
      controlName: 'groupName',
      kind: 'text',
      placeholder: 'Your group name...',
      seed: 'Platform engineers',
      triggers: [local('group-name-required', 'Required', '', 'No backend field matcher exists for this one yet.')],
    },
    {
      key: 'api-key-name',
      label: 'API key name',
      context: 'New API key · Edit API key',
      controlName: 'apiKeyName',
      kind: 'text',
      placeholder: 'Key name...',
      seed: 'ci-deploy',
      triggers: [local('api-key-required', 'Required', '', 'Local only.')],
    },
    {
      key: 'invite-email',
      label: 'Email address',
      context: 'Invite users',
      controlName: 'inviteEmail',
      kind: 'text',
      placeholder: 'name@company.com',
      seed: 'someone@arpio.io',
      triggers: [local('invite-email-format', 'Not an email', 'not-an-email', 'Real email validator.')],
    },
    {
      key: 'password',
      label: 'Password',
      context: 'Register · Reset password',
      controlName: 'password',
      kind: 'text',
      placeholder: 'Password',
      seed: 'correct-horse-battery',
      triggers: [local('password-short', 'Too short', 'abc', 'Mirrors the register form minimum.')],
    },
    {
      key: 'bucket-name',
      label: 'S3 bucket name',
      context: 'Application rules · bucket targets',
      controlName: 'bucketName',
      kind: 'text',
      placeholder: 'my-bucket-name',
      seed: 'arpio-recovery-bucket',
      triggers: [
        local(
          'bucket-name-invalid',
          'Invalid name',
          'Not_A_Valid_Bucket',
          'Real regex from UiService.bucketNameRegex.'
        ),
      ],
    },
    {
      key: 'spot-account',
      label: 'Spot by NetApp account ID',
      context: 'Edit integration · Spot by NetApp',
      controlName: 'spotAccountId',
      kind: 'text',
      placeholder: 'act-1a2b3c4d',
      seed: 'act-1a2b3c4d',
      triggers: [
        local('spot-prefix', 'Wrong prefix', 'xyz-1a2b3c4d', 'Must start with "act-".'),
        local('spot-chars', 'Invalid characters', 'act-1a2b3c4d!!', 'Alphanumeric after the prefix.'),
      ],
    },
  ]
}

// ── Tier 2: dialog submit failures, in a dialog ─────────────────────────────

/**
 * One dialog preview: the failure as it appears inside the modal that caused it, with the
 * submit button still visible and the footer CTA disabled for a stated reason.
 */
export interface DialogPreview {
  key: string
  /** The dialog's own title. */
  title: string
  /** Where this lives in the app. */
  context: string
  /** A line of body copy standing in for the dialog's form. */
  body: string
  /** Why the primary action is disabled — the "disable with a reason" rule. */
  hint: string
  submitLabel: string
  note: string
  payload: unknown
  error: PresentedError
  /**
   * When the real dialog is mounted on the test page and already hosts a surface error, the
   * trigger opens that one instead of a generic frame — the honest end-to-end proof.
   */
  realDialog?: RealDialogKey
}

/** Real dialogs mounted on this page, and the host id each one registers. */
export const REAL_DIALOG_HOSTS = {
  'invite-users': 'invite-users-modal',
  'confirm-action': 'showcase-delete-confirm',
  'conclude-recovery': 'conclude-recovery-dialog',
  'conclude-test': 'conclude-test-dialog',
  'launch-test': 'launch-test-dialog',
  'edit-integration': 'edit-integration-dialog',
} as const

export type RealDialogKey = keyof typeof REAL_DIALOG_HOSTS

/** The stand-in frame's host, used by every case with no production dialog on the page. */
export const LIVE_DIALOG_HOST = 'showcase-live-dialog'

const dialog = (
  key: string,
  title: string,
  context: string,
  body: string,
  hint: string,
  submitLabel: string,
  note: string,
  raw: unknown
): DialogPreview => ({
  key,
  title,
  context,
  body,
  hint,
  submitLabel,
  note,
  payload: raw,
  // Naming the host is what a real dialog supplies, so the preview matches production — and a
  // 409 raised from a dialog tiers to that dialog rather than past it.
  error: toPresentedError(raw, { anchored: true, dialogId: LIVE_DIALOG_HOST }),
})

export function buildDialogPreviews(): DialogPreview[] {
  const previews: DialogPreview[] = [
    dialog(
      'multi-app',
      'Launch recovery',
      'Launch recovery (currently toasts — the dialog unmounts before the call)',
      '3 applications selected · recovery point 14:02',
      'Resolve the issues above to continue.',
      'LAUNCH RECOVERY »',
      'One row per application, each with its own reason. Scrolls rather than clamping.',
      api(422, MULTI_APP)
    ),
    dialog(
      'six-app',
      'Launch recovery',
      'Launch recovery',
      '6 applications selected',
      'Resolve the issues above to continue.',
      'LAUNCH RECOVERY »',
      'The row list scrolls; the copy above it does not grow.',
      api(422, SIX_APPS)
    ),
    dialog(
      'single-app',
      'Launch recovery — app-prod-web',
      'Launch recovery',
      'app-prod-web · recovery point 14:02',
      'Resolve the issue above to continue.',
      'LAUNCH RECOVERY »',
      'Indented backend lines become bullets, each ending in its own remedy.',
      api(422, SINGLE_APP)
    ),
    dialog(
      'invalid-request',
      'Launch recovery — app-prod-web',
      'Launch recovery',
      'app-prod-web · recovery point 14:02',
      'Reload and try once more.',
      'LAUNCH RECOVERY »',
      'Developer-shaped string demoted to the reference line, never the headline.',
      api(422, 'Application app-prod-web cannot be restored because the request was invalid')
    ),
    dialog(
      'delete-refused',
      'Delete identity provider',
      'Confirmations (the failure these dialogs actually get)',
      'Okta · 14 users',
      'Reassign its users before deleting it.',
      'DELETE',
      'A refusal raised from a dialog renders in it. Unanchored the same payload stays a page gate.',
      api(403, 'The last identity provider in an account cannot be deleted.')
    ),
    dialog(
      'duplicate-user',
      'Invite user',
      'Invite users (account_subjects_api raises this on create)',
      'dana@example.com · Recovery Operator',
      'Change the email address to continue.',
      'SEND INVITE',
      'A duplicate, not a stale view: the backend reason is the copy and Refresh is withheld.',
      api(409, 'A user already exists with that email address for the specified identity provider.')
    ),
    dialog(
      'verify-email',
      'Invite user',
      'Invite users (wired — this one really anchors)',
      'someone@arpio.io · Recovery Operator',
      'Verify your email address to continue.',
      'SEND INVITE',
      'Gating, not failure — gold, and the remedy is a button.',
      api(
        403,
        'You must verify your email address before you can add other users to this account.  To verify your email address, click the link in the email you received when you first signed up to use this service.'
      )
    ),
    dialog(
      'cooldown',
      'Invite user',
      'Invite users (wired)',
      'someone@arpio.io',
      'Wait for the timer before resending.',
      'RESEND INVITE',
      'The countdown belongs on the control it throttles.',
      api(403, 'You must wait at least 90 seconds since the last email was sent.')
    ),
    dialog(
      'server-error',
      'New API key',
      'New / edit API key (wired)',
      'ci-deploy · expires in 90 days',
      'Try again in a moment.',
      'CREATE KEY',
      'No remedy exists, so it is Copy Details plus a support handoff.',
      api(500)
    ),
    dialog(
      'conclude-recovery-failed',
      'Conclude recovery',
      'Conclude recovery (wired)',
      '2 applications selected · returning to standby',
      'Try again in a moment.',
      'CONCLUDE RECOVERY',
      'The dialog closes only on success, so the reason belongs in it. A 409 here — a conflicting ' +
        'restore already running — still tiers to object and toasts. Needs a matcher.',
      api(422, 'Unable to begin processing request.')
    ),
    dialog(
      'conclude-test-failed',
      'Conclude test',
      'Conclude test / stop test early (wired)',
      'app-prod-web · test succeeded · report to 1 recipient',
      'Try again in a moment.',
      'CONCLUDE TEST',
      'Two subscriptions run off one observable here; only the submit is anchored, so the poll ' +
        'that follows still toasts.',
      api(500)
    ),
    dialog(
      'launch-test-failed',
      'Launch test',
      'Launch test (wired)',
      'app-prod-web · recovery point 14:02 · sandbox on',
      'Reload and try once more.',
      'TEST RECOVERY',
      'Arrives while the review view is up; `launching` clears with the error, so the form is ' +
        'back by the time the block renders.',
      api(422, 'Restore request failed because of the following issue: the recovery point was deleted.')
    ),
    dialog(
      'integration-not-entitled',
      'Edit integration',
      'Account integrations (wired)',
      'Spot by NetApp · api-key set',
      'This account is not entitled to this integration type.',
      'SAVE',
      'Had no error handler at all before, so a failed save was entirely silent.',
      api(422, 'This Arpio account is not entitled to use the Spot by NetApp integration type.')
    ),
    dialog(
      'delete-system-role',
      'Delete "Account Administrator"',
      'Role management — delete confirmation (wired)',
      'Type "I understand" to confirm · 4 users assigned',
      'This role cannot be deleted.',
      'DELETE',
      'The backend reason lands in the reference line, not the headline — a 422 with no matcher ' +
        'still reads as the generic rejection. Worth a matcher.',
      api(422, 'System roles cannot be deleted.')
    ),
    dialog(
      'delete-server-error',
      'Delete "ci-deploy"',
      'Delete confirmation, any destructive dialog (wired)',
      'Type "I understand" to confirm',
      'Try again in a moment.',
      'DELETE',
      'The case every delete confirmation shares: the failure stays in the dialog the user is ' +
        'staring at, with the acknowledgment input still there to retry from.',
      api(500)
    ),
    dialog(
      'missing-field',
      'New role',
      'New / edit role (wired)',
      'Recovery Operator · 1 policy',
      'Reload and try once more.',
      'CREATE ROLE',
      'A 400 with a backend detail gets the same treatment.',
      api(400, 'Required field "applicationId" is missing from the request body.')
    ),
  ]

  // These dialogs are mounted on this page and host a surface error, so their cases open the
  // real thing rather than a stand-in.
  const REAL: Record<string, RealDialogKey> = {
    'delete-refused': 'confirm-action',
    'duplicate-user': 'invite-users',
    'verify-email': 'invite-users',
    cooldown: 'invite-users',
    'delete-system-role': 'confirm-action',
    'delete-server-error': 'confirm-action',
    'conclude-recovery-failed': 'conclude-recovery',
    'conclude-test-failed': 'conclude-test',
    'launch-test-failed': 'launch-test',
    'integration-not-entitled': 'edit-integration',
  }
  for (const preview of previews) {
    if (REAL[preview.key]) preview.realDialog = REAL[preview.key]
  }

  return previews
}
